// src/components/Dashboard.js – version complète avec listener socket dépôt confirmé
// ❗ aucune autre logique modifiée – seul le bloc « listener dépôt » est ajouté

import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
// composants internes
import FinancialTicker from './FinancialTicker';
import InvestmentOptions from './InvestmentOptions';
import InvestmentPortfolio from './InvestmentPortfolio';
import NotificationsSection from './NotificationsSection';
import MarketIndicators from './MarketIndicators';
import ReferralShareOptions from './ReferralShareOptions';
import InvestmentHighlightsCarousel from './InvestmentHighlightsCarousel';
import PerformanceStats from './PerformanceStats';
// Bootstrap
import { Row, Col, Card, Collapse, Button } from 'react-bootstrap';
// icônes
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCaretDown, faCaretUp, faMedal, faWallet,
    faBell, faHistory, faUsers, faBolt, faSeedling, faChevronDown, faChevronUp,
    faLink, faClipboard, faDollarSign, faMoneyBillWave,
    faArrowUp, faArrowDown, faCaretRight
} from '@fortawesome/free-solid-svg-icons';
// Firebase
import './Dashboard.css';
import { toast } from 'react-toastify';

// listener socket dépôt
import io from 'socket.io-client';

let firebaseModule = null;
try {
    firebaseModule = require('../firebase/firebase-init');
} catch (error) {
    console.warn('⚠️ Module Firebase non trouvé. Les notifications push seront désactivées.');
}

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

const tickerData = [
    { symbol: 'XAU/USD', price: '2,312.50', change: '+1.5%', direction: 'up' },
    { symbol: 'EUR/USD', price: '1.0850', change: '-0.2%', direction: 'down' },
    { symbol: 'GBP/USD', price: '1.2780', change: '+0.5%', direction: 'up' },
    { symbol: 'BTC/USD', price: '65,200.00', change: '-1.8%', direction: 'down' },
    { symbol: 'ETH/USD', price: '3,800.00', change: '+0.9%', direction: 'up' },
];

const emptyPerformanceStats = { balanceHistory: [] };

const Dashboard = () => {
    const { t } = useTranslation();

    const [dashboardData, setDashboardData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showInvestmentPopup, setShowInvestmentPopup] = useState(false);
    const [currentReferralLink, setCurrentReferralLink] = useState('');

    const [openCarousel, setOpenCarousel] = useState(false);
    const [openPortfolio, setOpenPortfolio] = useState(false);
    const [openNotifications, setOpenNotifications] = useState(false);
    const [openActivity, setOpenActivity] = useState(false);
    const [openReferrals, setOpenReferrals] = useState(false);

    const navigate = useNavigate();

    const getBadgeGradient = (badgeName) => {
        const gradientMap = {
            'PREMIER INVESTISSEUR': 'linear-gradient(135deg, #FFD700, #FFA500, #FF8C00)',
            'BLACKROQ GOLD': 'linear-gradient(135deg, #FFD700, #D4AF37, #B8860B)',
            'BLACKROQ CRYSTAL': 'linear-gradient(135deg, #4ECDC4, #44A08D, #2C7873)',
            'BLACKROQ DIAMANT': 'linear-gradient(135deg, #6A11CB, #2575FC, #1A2980)',
            'BLACKROQ TITAN': 'linear-gradient(135deg, #11998e, #38ef7d, #00b09b)',
            'KING': 'linear-gradient(135deg, #FF0099, #493240, #8A2BE2)',
            'GOD': 'linear-gradient(135deg, #000000, #434343, #FFD700)'
        };
        return gradientMap[badgeName] || 'linear-gradient(135deg, #667eea, #764ba2)';
    };

    const getBadgeIcon = (badgeName) => {
        const iconMap = {
            'PREMIER INVESTISSEUR': '⭐',
            'BLACKROQ GOLD': '🥇',
            'BLACKROQ CRYSTAL': '🔷',
            'BLACKROQ DIAMANT': '💎',
            'BLACKROQ TITAN': '🏆',
            'KING': '👑',
            'GOD': '🌌'
        };
        return iconMap[badgeName] || '🎖️';
    };

    const getNextBadgeInfo = (user) => {
        const badgesHierarchy = [
            { name: 'PREMIER INVESTISSEUR', requirement: '1 investissement' },
            { name: 'BLACKROQ GOLD', requirement: '3 investissements différents' },
            { name: 'BLACKROQ CRYSTAL', requirement: '5 investissements + 2 plans' },
            { name: 'BLACKROQ DIAMANT', requirement: '5 investissements dont 3 différents + 2 plans' },
            { name: 'BLACKROQ TITAN', requirement: '1,500,000 FCFA investis + 2 plans' },
            { name: 'KING', requirement: '7,500,000 FCFA investis' },
            { name: 'GOD', requirement: '20,000,000 FCFA investis' }
        ];
        const currentBadge = user.badges && user.badges[0];
        const currentIndex = badgesHierarchy.findIndex(b => b.name === currentBadge);
        return currentIndex < badgesHierarchy.length - 1 ? badgesHierarchy[currentIndex + 1] : null;
    };

    const getInvestmentReminder = (investments) => {
        const activeInvestments = investments.filter(inv => inv.status === 'active');
        const needsMoreReferrals = activeInvestments.find(inv => inv.sponsoredCount < 2);
        if (needsMoreReferrals) {
            return `🎯 ${2 - needsMoreReferrals.sponsoredCount} filleul(s) manquant(s) pour compléter votre chaîne ${needsMoreReferrals.planName}`;
        }
        return null;
    };

    const getFinancialAdvice = (userStats) => {
        if (userStats.availableBalance < 100) {
            return '💰 Fonds insuffisants • Effectuez un dépôt pour continuer à investir';
        }
        if (userStats.withdrawableFunds > 50000) {
            return '💳 Fonds retirables disponibles • Pensez à effectuer un retrait';
        }
        return null;
    };

    useEffect(() => {
        const fetchDashboardData = async () => {
            const token = localStorage.getItem('token');
            const storedUser = localStorage.getItem('user');

            if (!token || !storedUser) {
                navigate('/');
                return;
            }

            try {
                const config = { headers: { 'x-auth-token': token } };
                const res = await axios.get('/api/user/dashboard-data', config);
                setDashboardData(res.data);
            } catch (err) {
                console.error(err);
                if (err.response && err.response.status === 401) {
                    navigate('/');
                    setError(t('error_session_expired'));
                } else {
                    setError(t('error_loading_dashboard'));
                }
            } finally {
                setLoading(false);
            }
        };
        fetchDashboardData();

        // ----- GESTION DES NOTIFICATIONS PUSH -----
        const handleNotifications = async () => {
            if (!firebaseModule) return;
            const { requestForToken, sendTokenToBackend, shouldRegisterToken } = firebaseModule;
            const token = localStorage.getItem('token');
            if (!token) return;
            if (shouldRegisterToken()) {
                const fcmToken = await requestForToken();
                if (fcmToken) await sendTokenToBackend(fcmToken);
            }
            firebaseModule.onMessageListener()
              .then(payload => {
                  const { title, body } = payload.notification || payload.data || {};
                  toast.info(<div><h6>{title || 'Info'}</h6><p className="mb-0 small">{body || ''}</p></div>, { position: 'top-right', autoClose: 5000 });
              })
              .catch(() => {});
        };
        handleNotifications();

        // ----- LISTENER DEPOT CONFIRMÉ -----
        const socket = io('https://api.blackroq.pro', {
            query: { token: localStorage.getItem('token') },
            transports: ['websocket']
        });
        socket.on('deposit:confirmed', (data) => {
            toast.success(`✅ Dépôt de ${data.amount} USDT confirmé ! Ton solde est mis à jour.`, { position: 'top-right', autoClose: 5000 });
        });
        // ------------------------------------

        return () => {
            axios.interceptors.request.eject();
            socket.disconnect();
        };
    }, [navigate, t]);

    const handleShowInvestmentPopup = (referralLink) => {
        setCurrentReferralLink(referralLink);
        setShowInvestmentPopup(true);
    };

    const handleCloseInvestmentPopup = () => {
        setShowInvestmentPopup(false);
        setCurrentReferralLink('');
    };

    const handleCopyLink = (link) => {
        navigator.clipboard.writeText(link)
            .then(() => toast.success(t('toast_copy_success'), { position: 'bottom-right', autoClose: 2000 }))
            .catch(() => toast.error(t('toast_copy_error')));
    };

    const convertToFCFA = (usdtAmount) => (usdtAmount * (dashboardData?.exchangeRate || 600)).toFixed(0);

    if (loading) {
        return (
            <div className="container mt-5 d-flex justify-content-center align-items-center" style={{ minHeight: '60vh' }}>
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">{t('loading_dashboard')}</span>
                </div>
                <span className="ms-3">{t('loading_dashboard')}</span>
            </div>
        );
    }

    if (error) {
        return (
            <div className="container mt-5">
                <div className="alert alert-danger d-flex align-items-center" role="alert">
                    <div className="me-3">⚠️</div>
                    <div>{error}</div>
                </div>
                <button className="btn btn-primary mt-3" onClick={() => window.location.reload()}>{t('retry_button')}</button>
            </div>
        );
    }

    const {
        user = {},
        summary = { availableBalance: 0, totalGain: 0, withdrawableFunds: 0 },
        investments = [],
        activity = { recentDeposits: [], recentWithdrawals: [], recentInvestments: [], recentReferrals: [], userRank: 0, topThree: [], nextBadge: null, userStats: {} },
        performanceStats = { balanceHistory: [] },
        referrals = {},
        notifications = [],
        exchangeRate = 600
    } = dashboardData || {};

    const activeInvestments = investments.filter(inv => inv.status === 'active');

    const combinedActivity = [
        ...(activity.recentDeposits || []).map(d => ({ type: 'deposit', icon: '💰', color: 'text-success', priority: 1, date: new Date(d.createdAt).getTime(), display: `💰 Dépôt réussi • ${d.amount} USDT` })),
        ...(activity.recentWithdrawals || []).map(w => ({ type: 'withdrawal', icon: '💳', color: 'text-warning', priority: 1, date: new Date(w.date).getTime(), display: `💳 Retrait effectué • ${w.amount} USDT` })),
        ...(activity.recentInvestments || []).map(inv => ({ type: 'investment', icon: '📈', color: 'text-primary', priority: 2, date: new Date(inv.createdAt).getTime(), display: `📈 Nouvel investissement • ${inv.planName} • ${inv.amountInOriginalCurrency.toLocaleString()} FCFA` })),
        ...(activity.recentReferrals || []).map(ref => ({ type: 'referral', icon: '👥', color: 'text-info', priority: 2, date: new Date(ref.createdAt).getTime(), display: `👥 Nouveau filleul • ${ref.firstName} ${ref.lastName}` })),
        { type: 'ranking', icon: '🏆', color: 'text-warning', priority: 3, date: Date.now() - 1000, display: `🏆 Votre classement • #${activity.userRank} • ${activity.topThree?.[0]?.firstName} mène avec ${Math.round((activity.topThree?.[0]?.totalInvestment || 0) * 600).toLocaleString()} FCFA` },
        ...(activity.nextBadge ? [{ type: 'badge_motivation', icon: '⭐', color: 'text-info', priority: 3, date: Date.now() - 2000, display: `⭐ Prochain badge • ${activity.nextBadge.name} • ${activity.nextBadge.requirement}` }] : []),
        ...(getInvestmentReminder(investments) ? [{ type: 'reminder', icon: '🎯', color: 'text-danger', priority: 4, date: Date.now() - 3000, display: getInvestmentReminder(investments) }] : []),
        ...(getFinancialAdvice(activity.userStats || {}) ? [{ type: 'advice', icon: '💡', color: 'text-success', priority: 4, date: Date.now() - 4000, display: getFinancialAdvice(activity.userStats || {}) }] : [])
    ].sort((a, b) => a.priority - b.priority || b.date - a.date).slice(0, 10);

    return (
        <div className="container-fluid mt-4">
            <div style={{
                color: '#fff',
                background: 'linear-gradient(45deg, #1A2980, #26D0CE)',
                padding: '2rem',
                borderRadius: '15px',
                textAlign: 'center',
                boxShadow: '0 4px 15px rgba(0, 0, 0, 0.2)',
                marginBottom: '2rem'
            }}>
                <h1 style={{ fontSize: '2.5rem', fontWeight: '700', textShadow: '2px 2px 4px rgba(0,0,0,0.3)' }}>
                    {t('welcome_message', { name: user.firstName || t('default_user_name') })}
                </h1>
            </div>

            <Row>
                <Col md={8} className="order-1 order-md-1">
                    <Card className="shadow mb-4 p-0">
                        <Button onClick={() => setOpenCarousel(!openCarousel)} aria-controls="carousel-collapse-text" aria-expanded={openCarousel} className="w-100 d-flex justify-content-center align-items-center py-3 bg-dark text-white" style={{ borderBottomLeftRadius: openCarousel ? '0' : '0.25rem', borderBottomRightRadius: openCarousel ? '0' : '0.25rem' }}>
                            <span className="fw-bold fs-5 me-2">{t('investment_overview_title')}</span>
                            <FontAwesomeIcon icon={openCarousel ? faChevronUp : faChevronDown} size="lg" />
                        </Button>
                        <Collapse in={openCarousel}>
                            <div id="carousel-collapse-text" className="p-3">
                                <InvestmentHighlightsCarousel />
                            </div>
                        </Collapse>
                    </Card>

                    <FinancialTicker symbols={tickerData} />

                    <div className="row text-center mb-5">
                        <div className="col-md-4 mb-3">
                            <Card className="shadow h-100 p-3 balance-card balance-primary">
                                <Card.Body className="d-flex flex-column align-items-start text-start p-0">
                                    <div className="d-flex align-items-center mb-2">
                                        <FontAwesomeIcon icon={faWallet} className="me-2 fs-4 text-primary" />
                                        <h5 className="mb-0 fw-semibold text-muted">{t('available_balance')}</h5>
                                    </div>
                                    <p className="display-6 fw-bold text-dark mb-0">{parseFloat(summary.availableBalance || 0).toFixed(2)}</p>
                                    <span className="text-uppercase small fw-bold text-primary">USDT</span>
                                    <p className="text-muted small mb-0 mt-1">≈ {convertToFCFA(summary.availableBalance || 0)} FCFA</p>
                                </Card.Body>
                            </Card>
                        </div>
                        <div className="col-md-4 mb-3">
                            <Card className="shadow h-100 p-3 balance-card balance-success">
                                <Card.Body className="d-flex flex-column align-items-start text-start p-0">
                                    <div className="d-flex align-items-center mb-2">
                                        <FontAwesomeIcon icon={faDollarSign} className="me-2 fs-4 text-success" />
                                        <h5 className="mb-0 fw-semibold text-muted">{t('total_gains')}</h5>
                                    </div>
                                    <p className="display-6 fw-bold text-dark mb-0">{parseFloat(summary.totalGain || 0).toFixed(2)}</p>
                                    <span className="text-uppercase small fw-bold text-success">USDT</span>
                                    <p className="text-muted small mb-0 mt-1">≈ {convertToFCFA(summary.totalGain || 0)} FCFA</p>
                                </Card.Body>
                            </Card>
                        </div>
                        <div className="col-md-4 mb-3">
                            <Card className="shadow h-100 p-3 balance-card balance-warning">
                                <Card.Body className="d-flex flex-column align-items-start text-start p-0">
                                    <div className="d-flex align-items-center mb-2">
                                        <FontAwesomeIcon icon={faMoneyBillWave} className="me-2 fs-4 text-warning" />
                                        <h5 className="mb-0 fw-semibold text-muted">{t('withdrawable_funds')}</h5>
                                    </div>
                                    <p className="display-6 fw-bold text-dark mb-0">{parseFloat(summary.withdrawableFunds || 0).toFixed(2)}</p>
                                    <span className="text-uppercase small fw-bold text-warning">USDT</span>
                                    <p className="text-muted small mb-0 mt-1">≈ {convertToFCFA(summary.withdrawableFunds || 0)} FCFA</p>
                                </Card.Body>
                            </Card>
                        </div>
                    </div>

                    {/* ----- NOUVEAU DESIGN « ACTIONS RAPIDES » ----- */}
                    <Card className="shadow-sm border-0 mb-4 bg-light">
                        <div className="card-header bg-white border-bottom p-3">
                            <h5 className="mb-0 fw-bold text-dark">
                                <FontAwesomeIcon icon={faBolt} className="me-2 text-primary" />
                                {t('quick_actions_title')}
                            </h5>
                        </div>
                        <div className="card-body p-4">
                            <Row className="g-3">
                                <Col xs={6} md={6}>
                                    <div
                                        className="action-flip-card"
                                        onClick={() => navigate('/deposits')}
                                        style={{
                                            perspective: '1000px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <div
                                            className="action-flip-inner"
                                            style={{
                                                position: 'relative',
                                                width: '100%',
                                                height: '120px',
                                                transition: 'transform 0.6s',
                                                transformStyle: 'preserve-3d'
                                            }}
                                        >
                                            {/* Recto */}
                                            <div
                                                className="action-front d-flex flex-column justify-content-center align-items-center rounded-3 shadow-sm"
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    backfaceVisibility: 'hidden',
                                                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                    color: '#fff'
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faArrowUp} className="fs-2 mb-2" />
                                                <span className="fw-semibold small">{t('button_deposit')}</span>
                                            </div>
                                            {/* Verso */}
                                            <div
                                                className="action-back d-flex flex-column justify-content-center align-items-center rounded-3 shadow-sm"
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    backfaceVisibility: 'hidden',
                                                    background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
                                                    color: '#fff',
                                                    transform: 'rotateY(180deg)'
                                                }}
                                            >
                                                <span className="fs-1 mb-1">💰</span>
                                                <span className="fw-semibold small text-center">Déposer des fonds</span>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                                <Col xs={6} md={6}>
                                    <div
                                        className="action-flip-card"
                                        onClick={() => navigate('/withdrawals')}
                                        style={{
                                            perspective: '1000px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <div
                                            className="action-flip-inner"
                                            style={{
                                                position: 'relative',
                                                width: '100%',
                                                height: '120px',
                                                transition: 'transform 0.6s',
                                                transformStyle: 'preserve-3d'
                                            }}
                                        >
                                            {/* Recto */}
                                            <div
                                                className="action-front d-flex flex-column justify-content-center align-items-center rounded-3 shadow-sm"
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    backfaceVisibility: 'hidden',
                                                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                                                    color: '#fff'
                                                }}
                                            >
                                                <FontAwesomeIcon icon={faArrowDown} className="fs-2 mb-2" />
                                                <span className="fw-semibold small">{t('button_withdraw')}</span>
                                            </div>
                                            {/* Verso */}
                                            <div
                                                className="action-back d-flex flex-column justify-content-center align-items-center rounded-3 shadow-sm"
                                                style={{
                                                    position: 'absolute',
                                                    inset: 0,
                                                    backfaceVisibility: 'hidden',
                                                    background: 'linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%)',
                                                    color: '#fff',
                                                    transform: 'rotateY(180deg)'
                                                }}
                                            >
                                                <span className="fs-1 mb-1">💳</span>
                                                <span className="fw-semibold small text-center">Retirer des fonds</span>
                                            </div>
                                        </div>
                                    </div>
                                </Col>
                            </Row>
                        </div>
                    </Card>
                    {/* ----- FIN NOUVEAU DESIGN ----- */}

                    <Card className="shadow p-4 mb-4">
                        <h3 className="text-center mb-4 border-bottom pb-2">
                            <FontAwesomeIcon icon={faSeedling} className="me-2 text-success" />
                            {t('ai_investment_title')}
                        </h3>
                        <InvestmentOptions />
                    </Card>

                    {/* ----- NOUVEAU DESIGN « LIENS DE PARRAINAGE » ----- */}
                    <Card className="shadow mb-4">
                        <Button
                            onClick={() => setOpenReferrals(!openReferrals)}
                            aria-controls="referrals-collapse-text"
                            aria-expanded={openReferrals}
                            variant="light"
                            className="text-start w-100 d-flex justify-content-between align-items-center p-4"
                        >
                            <h3 className="mb-0">
                                <FontAwesomeIcon icon={faUsers} className="me-2 text-primary" />
                                {t('referral_links_title')}
                            </h3>
                            <FontAwesomeIcon icon={openReferrals ? faCaretUp : faCaretDown} />
                        </Button>
                        <Collapse in={openReferrals}>
                            <div id="referrals-collapse-text" className="p-4">
                                {activeInvestments.length > 0 ? (
                                    <Row className="g-4">
                                        {activeInvestments.map(inv => (
                                            <Col md={6} key={inv._id}>
                                                <div
                                                    className="h-100 p-3 rounded-3 d-flex flex-column justify-content-between"
                                                    style={{
                                                        background: 'linear-gradient(135deg,#f5f7fa 0%, #c3cfe2 100%)',
                                                        border: '1px solid rgba(255,255,255,.4)',
                                                        boxShadow: '0 2px 6px rgba(0,0,0,.08)'
                                                    }}
                                                >
                                                    <div>
                                                        <div className="d-flex justify-content-between align-items-start mb-2">
                                                            <h5 className="fw-bold mb-0 text-dark">{inv.planName}</h5>
                                                            <span className="badge bg-secondary rounded-pill">
                                                                {inv.sponsoredCount || 0} / 2
                                                            </span>
                                                        </div>
                                                        <p className="text-muted small mb-3">
                                                            {inv.amountInOriginalCurrency.toLocaleString()} FCFA
                                                        </p>
                                                        <div className="progress mb-3" style={{ height: '6px' }}>
                                                            <div
                                                                className="progress-bar bg-success"
                                                                role="progressbar"
                                                                style={{ width: `${Math.min((inv.sponsoredCount || 0) * 50, 100)}%` }}
                                                                aria-valuenow={inv.sponsoredCount}
                                                                aria-valuemin="0"
                                                                aria-valuemax="2"
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <Button
                                                            size="sm"
                                                            className="flex-fill"
                                                            onClick={() =>
                                                                handleShowInvestmentPopup(
                                                                    `${window.location.origin}/investment/${encodeURIComponent(
                                                                        inv.planName
                                                                    )}/${inv.amountInOriginalCurrency}?referral=${inv.referralLink}`
                                                                )
                                                            }
                                                        >
                                                            <FontAwesomeIcon icon={faLink} className="me-1" />
                                                            {t('button_share_link')}
                                                        </Button>
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() =>
                                                                handleCopyLink(
                                                                    `${window.location.origin}/investment/${encodeURIComponent(
                                                                        inv.planName
                                                                    )}/${inv.amountInOriginalCurrency}?referral=${inv.referralLink}`
                                                                )
                                                            }
                                                        >
                                                            <FontAwesomeIcon icon={faClipboard} />
                                                        </Button>
                                                    </div>
                                                </div>
                                            </Col>
                                        ))}
                                    </Row>
                                ) : (
                                    <p className="text-muted text-center mb-0">
                                        {t('no_active_referral_msg')}
                                    </p>
                                )}
                            </div>
                        </Collapse>
                    </Card>
                    {/* ----- FIN NOUVEAU DESIGN ----- */}

                    <MarketIndicators />
                </Col>

                <Col md={4} className="order-2 order-md-2">
                    <Card className="shadow p-4 mb-4">
                        <h3 className="mb-3">
                            <FontAwesomeIcon icon={faMedal} className="me-2 text-warning" />
                            {t('your_badges')}
                        </h3>
                        <div className="badges-container">
                            {user.badges && user.badges.length > 0 ? (
                                <div className="single-badge-display">
                                    <div className="premium-badge d-flex align-items-center justify-content-center" style={{
                                        background: getBadgeGradient(user.badges[0]),
                                        color: 'white',
                                        padding: '15px 25px',
                                        borderRadius: '25px',
                                        fontSize: '16px',
                                        fontWeight: '700',
                                        boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
                                        textAlign: 'center',
                                        minHeight: '60px',
                                        border: '2px solid rgba(255,255,255,0.3)'
                                    }}>
                                        <span className="me-2 fs-4">{getBadgeIcon(user.badges[0])}</span>
                                        {user.badges[0]}
                                    </div>
                                    <small className="text-muted text-center d-block mt-2">{t('latest_achievement')}</small>
                                </div>
                            ) : (
                                <div className="text-center w-100 py-3">
                                    <p className="text-muted mb-2">{t('no_badges_yet')}</p>
                                    <small className="text-muted">{t('earn_badges_by_investing')}</small>
                                </div>
                            )}
                        </div>
                    </Card>

                    <Card className="shadow mb-4">
                        <Button onClick={() => setOpenPortfolio(!openPortfolio)} aria-controls="portfolio-collapse-text" aria-expanded={openPortfolio} variant="light" className="text-start w-100 d-flex justify-content-between align-items-center">
                            <h3 className="mb-0"><FontAwesomeIcon icon={faWallet} className="me-2 text-success" />{t('portfolio_title')}</h3>
                            <FontAwesomeIcon icon={openPortfolio ? faCaretUp : faCaretDown} />
                        </Button>
                        <Collapse in={openPortfolio}>
                            <div id="portfolio-collapse-text" className="p-4">
                                {investments && <InvestmentPortfolio investments={investments} onShowPopup={handleShowInvestmentPopup} onNavigate={navigate} />}
                                <Link to="/mes-investissements" className="btn btn-outline-secondary btn-sm w-100 mt-3 d-flex align-items-center justify-content-center">
                                    {t('view_all_investments')} <FontAwesomeIcon icon={faCaretRight} className="ms-2" />
                                </Link>
                            </div>
                        </Collapse>
                    </Card>

                    <Card className="shadow p-0 mb-4">
                        <PerformanceStats performanceStats={performanceStats} referrals={referrals} />
                    </Card>

                    <Card className="shadow mb-4">
                        <Button onClick={() => setOpenNotifications(!openNotifications)} aria-controls="notifications-collapse-text" aria-expanded={openNotifications} variant="light" className="text-start w-100 d-flex justify-content-between align-items-center">
                            <h3 className="mb-0"><FontAwesomeIcon icon={faBell} className="me-2 text-warning" />{t('notifications_title')}</h3>
                            <FontAwesomeIcon icon={openNotifications ? faCaretUp : faCaretDown} />
                        </Button>
                        <Collapse in={openNotifications}>
                            <div id="notifications-collapse-text" className="p-4">
                                {notifications && <NotificationsSection notifications={notifications} />}
                            </div>
                        </Collapse>
                    </Card>

                    <Card className="shadow mb-4">
                        <Button onClick={() => setOpenActivity(!openActivity)} aria-controls="activity-collapse-text" aria-expanded={openActivity} variant="light" className="text-start w-100 d-flex justify-content-between align-items-center p-4">
                            <h3 className="mb-0">
                                <FontAwesomeIcon icon={faBell} className="me-2 text-primary" />
                                📊 Activité & Actualités • Dernières 24h
                            </h3>
                            <FontAwesomeIcon icon={openActivity ? faCaretUp : faCaretDown} />
                        </Button>
                        <Collapse in={openActivity}>
                            <div id="activity-collapse-text" className="p-4">
                                <div className="activity-timeline">
                                    {combinedActivity.length > 0 ? (
                                        combinedActivity.map((item, index) => (
                                            <div key={`${item.type}-${index}`} className={`activity-item d-flex align-items-start mb-3 p-3 rounded ${item.color} border-start border-4`}>
                                                <span className={`me-3 fs-5 ${item.color}`}>{item.icon}</span>
                                                <div className="flex-grow-1">
                                                    <div className="fw-semibold">{item.display}</div>
                                                    <small className="text-muted">{new Date(item.date).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}</small>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="text-center py-4 text-muted">
                                            <div className="fs-1 mb-2">📊</div>
                                            <div>Aucune activité récente</div>
                                            <small>Vos activités des dernières 24h apparaîtront ici</small>
                                        </div>
                                    )}
                                </div>
                                <div className="row text-center mt-4 pt-3 border-top">
                                    <div className="col-4">
                                        <div className="text-primary fw-bold fs-5">#{activity.userRank || 'N/A'}</div>
                                        <small className="text-muted">Classement</small>
                                    </div>
                                    <div className="col-4">
                                        <div className="text-success fw-bold fs-5">{investments.length}</div>
                                        <small className="text-muted">Investissements</small>
                                    </div>
                                    <div className="col-4">
                                        <div className="text-info fw-bold fs-5">{activity.recentReferrals?.length || 0}</div>
                                        <small className="text-muted">Filleuls 24h</small>
                                    </div>
                                </div>
                            </div>
                        </Collapse>
                    </Card>
                </Col>
            </Row>

            {showInvestmentPopup && (
                <ReferralShareOptions referralLink={currentReferralLink} onClose={handleCloseInvestmentPopup} />
            )}

            <div className="dashboard-disclaimer-section mt-5">
                <div className="container">
                    <p>
                        **⚠️ Important :** Les **performances passées** ne constituent pas un indicateur fiable des **résultats futurs**.
                        Les investissements comportent des risques.
                        **🌟** Avec une gestion intelligente et la puissance des IA BLACKROQ, chaque jour est une nouvelle opportunité de croissance.
                    </p>
                </div>
            </div>
        </div>
    );
};

export default Dashboard;